import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useGuide } from '../lib/lazyGuides';
import { NavIcon } from './NavIcons';

/**
 * "Controls" button in the bar under a game. The controls stay hidden (so they don't clutter the game)
 * until it's pressed; then a small panel opens above the bar. Pass `children` to show something other
 * than the game guide's controls list (the arcade games show the player's current key bindings).
 */
export function ControlsButton({ gameId, children }: { gameId: string; children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef<HTMLSpanElement>(null);
  const list = useGuide(gameId)?.controls ?? [];

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  if (!children && !list.length) return null;
  return (
    <span className="controls-pop" ref={ref}>
      <button type="button" className={`like-btn${open ? ' is-liked' : ''}`} aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
        <NavIcon name="keyboard" size={18} />
        <span>Controls</span>
      </button>
      {open && (
        <div className="controls-pop__panel" id={id} role="dialog" aria-label="Game controls">
          {children ?? (
            <ul>
              {list.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </span>
  );
}
